import type NodeFetchType from 'node-fetch';
import type { RequestInit } from 'node-fetch';
import type { ServerEnvConfigType } from '../config/ServerEnvConfigType';
import type { Any } from '../constant/any';
import { DocsSiteVisitType } from '../constant/DocsIdTypeData';
import { BizError } from '../constant/error/BizError';
import { ErrorCode } from '../constant/error/ErrorCode';
import { HttpStatusCodeMapErrorCode } from '../constant/error/HttpStatusCodeMapErrorCode';
import { getCid } from '../util/cid';
import { MemoryDataCache } from '../util/MemoryDataCache';
import { isClient } from '../util/ssr';
import { customDomainRequestPath } from './customDomainRequestPath';

function getApiBase(envConfig: ServerEnvConfigType) {
  let API_BASE = `${envConfig.SERVER_PROTOCOL}://${IS_RELEASE ? envConfig.SERVER_HOST : '127.0.0.1:5634'}`;
  if (process.env && process.env.NODE_ENV === 'development' && envConfig.DEBUG_API_BASE) {
    API_BASE = envConfig.DEBUG_API_BASE;
  }

  return API_BASE;
}

const requestCache = new MemoryDataCache<typeof Any>();

export enum RequestErrorMessageType {
  FailedFetch = 'failedFetch',
  ServerFailure = 'serverFailure',
  ServerErrorCode = 'serverErrorCode',
}

export function getCustomDomainUrl(visitType?: DocsSiteVisitType, subdirectory?: string) {
  if (visitType !== DocsSiteVisitType.CustomDomain) {
    return '';
  }

  return subdirectory ? `/${subdirectory}${customDomainRequestPath}` : customDomainRequestPath;
}

export function getDecoratedRequestUrl(
  url: string,
  options: {
    visitType?: DocsSiteVisitType;
    subdirectory?: string;
    envConfig: ServerEnvConfigType;
  },
) {
  if (!url.startsWith('http')) {
    if (options.visitType === DocsSiteVisitType.CustomDomain) {
      return `${getCustomDomainUrl(options.visitType, options.subdirectory)}${url}`;
    } else if (isClient && process.env.NODE_ENV !== 'development') {
      return `${url}`;
    } else {
      return `${getApiBase(options.envConfig)}${url}`;
    }
  }
  return url;
}

export async function request<T>(
  url: string,
  options: Omit<RequestInit, 'headers'> & {
    responseType?: 'json' | 'blob';
    cacheTime?: number;
    envConfig: ServerEnvConfigType;
    headers?: Record<string, string>;
    projectId: number;
    subdirectory?: string;
    visitType?: DocsSiteVisitType;
    onResponseHeaders?: (headers: Headers) => void;
    onErrorMessage?: (
      error:
        | { type: RequestErrorMessageType.FailedFetch }
        | { type: RequestErrorMessageType.ServerFailure }
        | { type: RequestErrorMessageType.ServerErrorCode; errorCode: string },
    ) => void;
    timeout?: number;
  },
  fetchFn: typeof fetch | typeof NodeFetchType,
): Promise<[BizError, undefined] | [undefined, T]> {
  try {
    const method = options?.method ?? 'GET';
    const cacheTime = options?.cacheTime ?? 0;
    const enableCache = cacheTime && method === 'GET';
    const responseType = options?.responseType ?? 'json';

    const newUrl = getDecoratedRequestUrl(url, options);
    if (enableCache) {
      const cacheResult = requestCache.get(newUrl);
      if (cacheResult) {
        return [undefined, cacheResult];
      }
    }

    const deviceId = getCid();
    const headers: Record<string, string> = {
      'X-Client-Version': '2.7.22',
      'Access-Control-Allow-Credentials': 'true',
      'Access-Control-Allow-Origin': '*',
      ...(options?.headers ?? {}),
    };
    if (deviceId) {
      headers['X-Device-Id'] = deviceId;
    }
    if (options.projectId) {
      headers['X-Project-Id'] = String(options.projectId);
    }
    if (newUrl.startsWith('https:')) {
      delete headers.host;
    }

    const timeout = options.timeout ?? options.envConfig.SERVER_REQUEST_TIMEOUT;
    const abortController = (() => {
      if (isClient || !timeout) {
        return undefined;
      }
      const ac = new AbortController();
      const timer = setTimeout(() => {
        ac.abort(`Request timeout: ${url}`);
      }, timeout);

      return {
        signal: ac.signal,
        clear: () => {
          clearTimeout(timer);
        },
      };
    })();

    const response = await fetchFn(newUrl, {
      credentials: 'include',
      ...options,
      headers,
      signal: abortController?.signal,
    } as any);

    abortController?.clear();

    if (!response.ok) {
      const errorCode = HttpStatusCodeMapErrorCode[response.status];
      if (response.status >= 500) {
        options.onErrorMessage?.({ type: RequestErrorMessageType.ServerFailure });
        return [
          new BizError(errorCode, `Request failed with status ${response.status}: ${url}`),
          undefined,
        ];
      }
    }

    if (responseType === 'blob' && response.status === 200) {
      return [undefined, response as T];
    } else {
      const result = await response.json();
      if (!result || typeof result !== 'object') {
        return [new BizError(new Error(`Invalid response: ${url}`)), undefined];
      }

      if (result.success !== true) {
        const errorCode = String(result.errorCode);
        options.onErrorMessage?.({ type: RequestErrorMessageType.ServerErrorCode, errorCode });

        if (Object.values(ErrorCode).includes(errorCode as ErrorCode)) {
          return [new BizError(errorCode as ErrorCode, `${url}`), undefined];
        }

        const httpStatusCodeMapErrorCode = HttpStatusCodeMapErrorCode[response.status];
        if (httpStatusCodeMapErrorCode) {
          options.onErrorMessage?.({ type: RequestErrorMessageType.ServerFailure });
          return [
            new BizError(
              httpStatusCodeMapErrorCode,
              `Request failed with status ${response.status}: ${url}`,
            ),
            undefined,
          ];
        }

        return [
          new BizError(new Error(`${result.errorMessage ?? 'Request failed'} ${url}`)),
          undefined,
        ];
      }

      options.onResponseHeaders?.(new Headers(Object.fromEntries(response.headers.entries())));

      if (enableCache && result.data) {
        requestCache.set(newUrl, result.data, cacheTime);
      }

      return [undefined, result.data];
    }
  } catch (error: any) {
    if (error instanceof Error) {
      if (['Failed to fetch', 'Load failed'].includes(error.message)) {
        options.onErrorMessage?.({ type: RequestErrorMessageType.ServerFailure });
      }
    }

    return [new BizError(error, url), undefined];
  }
}
